import { Color, Spacing } from '@persona-id/design-tokens';
import { MoreIcon } from '@persona-id/design-tokens/icons';
import { useMemo, useState } from 'react';
import {
  DragDropContext,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
} from 'react-beautiful-dnd';
import { Control, useFieldArray, UseFormSetValue } from 'react-hook-form';
import { BORDER_RADIUSES, COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';
import { v4 as uuid } from 'uuid';

import ContextMenu from 'dashboard/components/ContextMenu';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { OptionType } from 'dashboard/components/ReactSelect';
import { GraphNodeConfigFormData } from 'dashboard/forms/FormGraphNodeConfiguration/FormGraphNodeConfiguration';
import AddFunctionModal, {
  AddFunctionFormData,
} from 'dashboard/forms/FormGraphNodeConfiguration/NodeFunctionsConfigurationTab/AddFunctionModal';
import HoverableDraggable from 'dashboard/forms/FormGraphNodeConfiguration/NodeFunctionsConfigurationTab/HoverableDraggable';
import { PRESET_TYPE_TO_LABEL } from 'dashboard/forms/FormGraphNodeConfiguration/NodeFunctionsConfigurationTab/NodeFunctionsConfigurationTab';
import UpdateSourceModal from 'dashboard/forms/FormGraphNodeConfiguration/NodeFunctionsConfigurationTab/UpdateSourcesModal';
import { AccountType } from 'dashboard/models/account-type';
import {
  NodeFunction,
  NodeFunctionsSource,
  ServerPresetSummary,
  SourceObjectType,
} from 'dashboard/models/graph-backend';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker/Sticker';
import { Heading, Label, Paragraph } from 'design-system/components/Text/Text';
import { FieldSchemaType } from 'lib/fields';

interface NodeFunctionsNavigatorProps {
  control: Control<GraphNodeConfigFormData, object>;
  setValue: UseFormSetValue<GraphNodeConfigFormData>;
  nodeFunctions: NodeFunction[];
  setSelectedFunctionToken: (token: string) => void;
  selectedFunctionToken: string;
  accountTypes: AccountType[];
  presetSummaries: ServerPresetSummary[];
  disabled: boolean;
  cancelAutoMapper: () => void;
}

const StyledDiv = styled.div<{ isDraggingOver: boolean }>`
  ${({ isDraggingOver }) => css`
    background-color: ${isDraggingOver ? COLORS.indigo10 : COLORS.white};
  `};
  transition:
    background-color 0.4s ease-in-out,
    box-shadow 0.4s ease-in-out;
  border-radius: ${BORDER_RADIUSES.rounded};
`;

const Summary = styled.div<{ selected: boolean; isFirst: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
  padding: ${Spacing[3]} ${Spacing[2]};
  cursor: pointer;
  border-radius: ${Spacing[1]};
  ${({ selected }) => selected && `background-color: ${Color.BackgroundButtonTextActive};`}
  ${({ selected }) => selected && `color: ${Color.CorePurple700};`}
`;

const SourcesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Wrapper = styled.div`
  padding: ${Spacing[3]};
`;

const NumberSticker = styled(Sticker)`
  margin-right: ${Spacing[2]};
`;

const TextHeader = styled.div`
  padding: ${Spacing[5]} ${Spacing[5]} 0 ${Spacing[5]};
  border-bottom: 1px solid ${Color.BorderHairline};
`;

const getSourceName = (accountType: AccountType, source: NodeFunctionsSource) => {
  return source.path
    ? `${accountType.attributes.name}.${source.path}`
    : `${accountType.attributes.name}`;
};

const getSourceOptions = (accountTypes: AccountType[]): OptionType[] => {
  const options: OptionType[] = [];
  accountTypes.forEach((accountType) => {
    options.push({ label: accountType.attributes.name, value: accountType.id });

    const arrayTypes =
      accountType.attributes['field-schemas'].filter((fs) => fs.type === FieldSchemaType.Array) ||
      [];
    arrayTypes.forEach((arrayType) => {
      options.push({
        label: `${accountType.attributes.name}.${arrayType.key}`,
        value: `${accountType.id}.${arrayType.key}`,
      });
    });
  });
  return options;
};

const sourceToOptionString = (source: NodeFunctionsSource): string => {
  return source.path
    ? `${source['object-type-token']}.${source['path']}`
    : source['object-type-token'];
};

const updateSources = (
  existingSources: NodeFunctionsSource[],
  optionValues: string[],
  presetSummaries: ServerPresetSummary[],
  nodeFunctions: NodeFunction[],
): NodeFunctionsSource[] => {
  const optionStringToSource: Record<string, NodeFunctionsSource> = {};
  existingSources.forEach((source) => {
    optionStringToSource[sourceToOptionString(source as NodeFunctionsSource)] = source;
  });

  const updatedSources: NodeFunctionsSource[] = [];
  optionValues.forEach((value) => {
    if (value in optionStringToSource) {
      updatedSources.push(optionStringToSource[value]);
    } else {
      const parts = value.split('.');
      updatedSources.push({
        'object-type-token': parts[0],
        'object-type': SourceObjectType.Account,
        path: parts[1] || '',
        'function-mappings': nodeFunctions.map((nf) => {
          const preset = presetSummaries.find((ps) => ps.preset === nf.preset);
          return {
            token: nf.token,
            'param-mappings':
              preset?.['param-schema'].map((fieldSchema) => ({
                path: '',
                destination: fieldSchema.key,
              })) || [],
          };
        }),
        'node-key': [],
      });
    }
  });

  return updatedSources;
};

const NodeFunctionsNavigator = ({
  control,
  setValue,
  nodeFunctions,
  setSelectedFunctionToken,
  selectedFunctionToken,
  accountTypes,
  presetSummaries,
  disabled,
  cancelAutoMapper,
}: NodeFunctionsNavigatorProps) => {
  const { fields: sources, replace: replaceSources } = useFieldArray<
    GraphNodeConfigFormData,
    'nodeFunctionsConfig.sources',
    'id'
  >({
    control,
    name: 'nodeFunctionsConfig.sources',
  });

  const allSourceOptions = useMemo(() => getSourceOptions(accountTypes), [accountTypes]);
  const selectedOptions = useMemo(() => sources.map((s) => sourceToOptionString(s)), [sources]);
  const handleUpdateSources = (values: string[]) => {
    replaceSources(updateSources(sources, values, presetSummaries, nodeFunctions));
  };

  const handleAddFunction = (values: AddFunctionFormData) => {
    cancelAutoMapper();
    const newFunction = {
      token: `gnf_${uuid()}`,
      preset: values.presetType,
    };

    // NOTE(Tony): For now, we assume that each time a new function is added, it applies to all sources.
    // We plan to support removing functions from sources in the future.
    const newSources: NodeFunctionsSource[] = sources.map((source) => {
      const preset = presetSummaries.find((ps) => ps.preset === newFunction.preset);
      return {
        ...source,
        'function-mappings': [
          ...source['function-mappings'],
          {
            token: newFunction.token,
            'param-mappings':
              preset?.['param-schema'].map((fieldSchema) => ({
                path: '',
                destination: fieldSchema.key,
              })) || [],
          },
        ],
      };
    });

    setValue('nodeFunctionsConfig.functions', [...nodeFunctions, newFunction]);
    replaceSources(newSources);

    setSelectedFunctionToken(newFunction.token);
  };

  const handleDeleteFunction = (token: string) => {
    cancelAutoMapper();

    const functionIndex = nodeFunctions.findIndex((nf) => nf.token === token);
    const functionTokensAfterThis = nodeFunctions.slice(functionIndex + 1).map((nf) => nf.token);

    // Need to clear all param mappings for functions that come after the one being deleted.
    const newSources: NodeFunctionsSource[] = sources.map((source) => {
      return {
        ...source,
        'function-mappings': source['function-mappings']
          .filter((fm) => fm.token !== token)
          .map((fm) => {
            if (functionTokensAfterThis.includes(fm.token)) {
              const fn = nodeFunctions.find((nf) => nf.token === fm.token);
              const preset = presetSummaries.find((ps) => ps.preset === fn?.preset);

              return {
                token: fm.token,
                'param-mappings':
                  preset?.['param-schema'].map((fieldSchema) => ({
                    path: '',
                    destination: fieldSchema.key,
                  })) || [],
              };
            } else {
              return fm;
            }
          }),
        'node-key': source['node-key'].map((nk) => {
          return {
            key: nk.key,
            'function-token': '',
            path: '',
          };
        }),
      };
    });

    setValue(
      'nodeFunctionsConfig.functions',
      nodeFunctions.filter((nf) => nf.token !== token),
    );
    replaceSources(newSources);

    if (token === selectedFunctionToken) {
      setSelectedFunctionToken(nodeFunctions[0].token);
    }
  };

  const [isOpen, setIsOpen] = useState(false);

  const { move } = useFieldArray<GraphNodeConfigFormData, 'nodeFunctionsConfig.functions', 'id'>({
    control,
    name: 'nodeFunctionsConfig.functions',
  });

  const handleDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination || result.source.index === result.destination.index) {
      return;
    }
    move(result.source.index, result.destination.index);

    const minIdx = Math.min(result.source.index, result.destination.index);
    const functionTokensToClear = nodeFunctions.slice(minIdx).map((nf) => nf.token);

    // Clear mappings for all functions after the moved function.
    const newSources: NodeFunctionsSource[] = sources.map((source) => {
      return {
        ...source,
        'function-mappings': source['function-mappings'].map((fm) => {
          if (functionTokensToClear.includes(fm.token)) {
            const fn = nodeFunctions.find((nf) => nf.token === fm.token);
            const preset = presetSummaries.find((ps) => ps.preset === fn?.preset);

            return {
              token: fm.token,
              'param-mappings':
                preset?.['param-schema'].map((fieldSchema) => ({
                  path: '',
                  destination: fieldSchema.key,
                })) || [],
            };
          } else {
            return fm;
          }
        }),
        'node-key': source['node-key'].map((nk) => {
          return {
            key: nk.key,
            'function-token': '',
            path: '',
          };
        }),
      };
    });

    replaceSources(newSources);
  };

  const renderRouteSections = (
    droppableProvided: DroppableProvided,
    droppableSnapshot: DroppableStateSnapshot,
  ) => (
    <StyledDiv ref={droppableProvided.innerRef} isDraggingOver={droppableSnapshot.isDraggingOver}>
      {nodeFunctions.map((nodeFunction: NodeFunction, idx) => (
        <HoverableDraggable
          key={nodeFunction.token}
          draggableId={nodeFunction.token}
          index={idx}
          isDragDisabled={disabled}
        >
          <Summary
            isFirst={idx === 0}
            selected={nodeFunction.token === selectedFunctionToken}
            id={`node-functions-navigator__${nodeFunction.token}`}
            onClick={() => setSelectedFunctionToken(nodeFunction.token)}
          >
            <div>
              <NumberSticker
                variant={nodeFunction.token === selectedFunctionToken ? 'purple' : undefined}
              >
                {idx + 1}
              </NumberSticker>
              {PRESET_TYPE_TO_LABEL[nodeFunction.preset]}
            </div>
            <FeatureFlagged featureFlag='dashboard:graph-multiple-node-functions'>
              <ContextMenu
                variant='inverse'
                items={[
                  {
                    id: 'delete_function',
                    onClick: () => {
                      handleDeleteFunction(nodeFunction.token);
                    },
                    label: 'Delete Function',
                  },
                ]}
                renderItem={(item) => (
                  <ContextMenu.Item
                    key={item.id}
                    id={`node-functions-navigator__${item.id}_${nodeFunction.token}`}
                    onClick={() => {
                      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                      item.onClick();
                    }}
                    variant={item.variant}
                    disabled={disabled || nodeFunctions.length === 1}
                  >
                    {item.label}
                  </ContextMenu.Item>
                )}
              />
            </FeatureFlagged>
          </Summary>
        </HoverableDraggable>
      ))}
      {droppableProvided.placeholder}
    </StyledDiv>
  );

  return (
    <>
      <TextHeader>
        <Heading size='sm'>Functions</Heading>
        <Spacer height={1} />
        <Label size='md'>
          Reordering the list below might require remapping of outputs if being used in subsequent
          inputs.
        </Label>
        <Spacer height={1} />
        <div>
          <SourcesHeader>
            <Paragraph>Sources</Paragraph>
            {!disabled && (
              <UpdateSourceModal
                trigger={
                  <Button
                    id='node-functions-navigator__update-source-button'
                    width='auto'
                    variant='inverse'
                  >
                    <MoreIcon />
                  </Button>
                }
                options={allSourceOptions}
                selectedOptions={selectedOptions}
                onUpdateSources={handleUpdateSources}
              />
            )}
          </SourcesHeader>
          {sources
            .map((source) => {
              if (source['object-type'] === 'Account') {
                const accountType = accountTypes.find(
                  (at) => source['object-type-token'] === at.id,
                );
                return accountType && getSourceName(accountType, source);
              } else {
                return source['object-type'];
              }
            })
            .join(', ')}
        </div>
        <Spacer height={1} />
      </TextHeader>
      <Spacer height={1} />
      <Wrapper>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId='droppable'>{renderRouteSections}</Droppable>
        </DragDropContext>
        <FeatureFlagged featureFlag='dashboard:graph-multiple-node-functions'>
          <Spacer height={2} />
          <ModalWrapper
            handleCloseModal={() => setIsOpen(false)}
            isOpen={isOpen}
            shouldCloseOnEsc
            shouldCloseOnOverlayClick
            styleOverride={{
              content: {
                overflow: 'hidden',
              },
            }}
          >
            <AddFunctionModal
              onAddFunction={handleAddFunction}
              presetSummaries={presetSummaries}
              setIsOpen={setIsOpen}
            />
          </ModalWrapper>
          <Button
            id='node-functions-navigator__add-function-button'
            variant='basic'
            width='auto'
            type='button'
            onClick={() => setIsOpen(true)}
            disabled={disabled}
          >
            Add
          </Button>
        </FeatureFlagged>
      </Wrapper>
    </>
  );
};

export default NodeFunctionsNavigator;
